import { MutableRefObject } from "react";
import { useForm, Controller, FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TaskType } from "@myapp/contract";
import { FormSection } from "../../../../shared/ui/controls/FormSection/FormSection";
import { FieldWrapper } from "../../../../shared/ui";
import { FormInput } from "../../../../shared/ui/controls/FormInput";
import { FormSelect } from "../../../../shared/ui/controls/FormSelect";
import { EntitySearchSelect } from "../../../../shared/ui/search/EntitySearchSelect/EntitySearchSelect";
import { useUsersInfiniteQuery, useUserDetail } from "../../../../entities/user";
import {
  taskFormSchema,
  getDefaultValues,
  taskStatusOptions,
  type TaskFormValues,
} from "../model/taskForm.schema";

export type { TaskFormValues };

type TechnicianItem = { id: number; name: string };

// Adapter: converts User (string id) to TechnicianItem (number id)
function useTechnicianList(params: any) {
  const result = useUsersInfiniteQuery({ ...params, role: "WORKER" as const });
  const data = result.data
    ? {
      ...result.data,
      items: (result.data.items as Array<{ id: string; name: string }>).map(
        (u) => ({ id: Number(u.id), name: u.name })
      ),
    }
    : undefined;
  return { ...result, data };
}

// Adapter: detail hook accepts numeric id, converts to string for useUserDetail
function useTechnicianDetail(
  id: number | undefined,
  options?: { enabled?: boolean }
) {
  const result = useUserDetail(id !== undefined ? String(id) : undefined, options);
  const data = result.data
    ? { id: Number((result.data as any).id), name: (result.data as any).name as string }
    : undefined;
  return { ...result, data };
}

interface Props {
  taskType: TaskType;
  onSubmit: (values: TaskFormValues) => void;
  isLoading: boolean;
  submitRef: MutableRefObject<(() => void) | null>;
  initialValues?: TaskFormValues;
  mode?: "create" | "edit";
  /** Extra validation that runs before onSubmit. Return field→message map to block submit. */
  onValidate?: (values: TaskFormValues) => Partial<Record<FieldPath<TaskFormValues>, string>> | null;
}

export function TaskFormWidget({
  taskType,
  onSubmit,
  isLoading,
  submitRef,
  initialValues,
  mode = "create",
  onValidate,
}: Props) {
  const isErrands = taskType === "ERRANDS";
  const isEdit = mode === "edit";
  const plannedStartType = initialValues?.plannedStart?.includes("T") ? "datetime-local" : "date";

  const { control, handleSubmit, setError } = useForm<TaskFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(taskFormSchema) as any,
    defaultValues: initialValues ?? getDefaultValues(taskType),
  });

  submitRef.current = handleSubmit(
    (data) => {
      if (isLoading) return;
      if (onValidate) {
        const extraErrors = onValidate(data);
        if (extraErrors) {
          Object.entries(extraErrors).forEach(([field, message]) =>
            setError(field as FieldPath<TaskFormValues>, { message })
          );
          return;
        }
      }
      onSubmit(data);
    },
    (errors) => {
      console.error("[TaskForm] validation errors:", errors);
    }
  );

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => e.preventDefault()}
    >


      <FormSection title="Details">
        <FieldWrapper label="Titel*">
          <FormInput name="title" control={control} as="input" />
        </FieldWrapper>

        <FieldWrapper label="Beschreibung">
          <FormInput name="description" control={control} as="textarea" rows={3} />
        </FieldWrapper>
{/* 
        <FieldWrapper label="Datum">
          <FormInput name="plannedStart" control={control} type={plannedStartType} as="input" />
        </FieldWrapper>
 */}
        {/* ERRANDS only: Abgabefrist is required */}
        {isErrands && (
          <FieldWrapper label="Abgabefrist*">
            <FormInput name="deadline" control={control} type="date" as="input" />
          </FieldWrapper>
        )}

        {isEdit && (
          <FieldWrapper label="Status">
            <FormSelect
              name="status"
              control={control}
              options={taskStatusOptions}
            />
          </FieldWrapper>
        )}
      </FormSection>
      {isErrands || isEdit ? (
      <FormSection title="Plan">
        <FieldWrapper label="Handwerker">
          <Controller
            name="assignedTechnicianId"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <EntitySearchSelect<TechnicianItem>
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  useInfiniteHook={useTechnicianList}
                  useDetailHook={useTechnicianDetail}
                  searchParamName="search"
                  getLabel={(item) => item.name}
                  placeholder="Handwerker suchen..."
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FieldWrapper>
        <div className="flex flex-col gap-4 sm:flex-row sm:gap-5">
          <FieldWrapper label="Arbeitszeit (min)*">
            <FormInput
              name="plannedWorkDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              valueAsNumber
            />
          </FieldWrapper>
          <FieldWrapper label="Anfahrt (min)*">
            <FormInput
              name="plannedTravelDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              valueAsNumber
            />
          </FieldWrapper>
          <FieldWrapper label="Abfahrt (min)*">
            <FormInput
              name="plannedDepartureDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              valueAsNumber
            />
          </FieldWrapper>
        </div>
      </FormSection>
      ) : null}

          
      {/* Actual durations — edit mode only */}
      {isEdit && (
        <FormSection title="Tatsächliche Zeiten">

          <div className="flex flex-col gap-4 sm:flex-row sm:gap-5">
            <FieldWrapper label="Arbeitszeit (h)">
              <FormInput
                name="actualWorkDuration"
                control={control}
                type="number"
                as="input"
                valueAsNumber
                readOnly
              />
            </FieldWrapper>
            <FieldWrapper label="Anfahrt (h)">
              <FormInput
                name="actualTravelDuration"
                control={control}
                type="number"
                as="input"
                valueAsNumber
                readOnly
              />
            </FieldWrapper>
            <FieldWrapper label="Abfahrt (h)">
              <FormInput
                name="actualDepartureDuration"
                control={control}
                type="number"
                as="input"
                valueAsNumber
                readOnly
              />
            </FieldWrapper>
          </div>
        </FormSection>
      )}
    </form>
  );
}
